import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { PlatformService } from './platform.service';
import { environment } from '../environments/environment';

declare const grecaptcha: any;

const TRUSTED_CLIENT_KEY = '_sys_env_ready';
const RECAPTCHA_SUCCESS_COUNT_KEY = '_sys_init_stat';
const TRUST_THRESHOLD = 3;

export const securityGuard: CanActivateFn = async (route, state) => {
  const platform = inject(PlatformService);

  // If SSR (Server), skip the check so the page can pre-render
  if (!platform.isBrowserPlatform()) {
    return true;
  }

  // Trusted clients skip reCAPTCHA checks to reduce assessment calls.
  if (isTrustedClient(platform)) {
    return true;
  }

  const targetUrl = state.url;
  const urlSegments = targetUrl.split('/').filter((segment) => segment.length > 0);
  const routeWord = urlSegments[0] ? urlSegments[0].toUpperCase() : 'HOME';

  try {
    await waitForGrecaptcha();
    const recaptchaKey = environment.recaptchaVerifyKey ? environment.recaptchaVerifyKey : '6LfN3UEsAAAAAJAC0wtLjWpeTDLRzho-9U6s8737';
    const token = await grecaptcha.enterprise.execute(recaptchaKey, { action: routeWord });
    const result = await realVerifyAccess(token, routeWord);

    if (result.allow) {
      registerSuccessfulAssessment(platform);
      return true;
    } else {
      renderAccessDenied();
      return false;
    }
  } catch (err) {
    console.error('Security Gate Error:', err);
    renderAccessDenied();
    return false;
  }
};

/** Logic Helpers Moved from main.ts **/

async function realVerifyAccess(token: string, path: string) {
  const auth = environment.apiKey ? `?key=${environment.apiKey}` : '';
  const verfyurl = environment.verifyUrl + auth;
  const res = await fetch(verfyurl, {
    method: 'POST',
    headers: { 'accept': 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, recaptcha_action: path }),
  });
  return res.json();
}

function waitForGrecaptcha(timeout = 3000): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (typeof grecaptcha !== 'undefined' && grecaptcha.enterprise) {
        grecaptcha.enterprise.ready(() => resolve());
        return;
      }
      if (Date.now() - start > timeout) {
        reject(new Error('reCAPTCHA Enterprise failed to initialize.'));
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });
}

function renderAccessDenied(): void {
  document.body.innerHTML = `
    <div style="display: flex; height: 100vh; align-items: center; justify-content: center; font-family: Arial, sans-serif; background: #f5f5f5;">
      <div style="text-align: center;">
        <h1>Access Denied</h1>
        <p>We're sorry, you've been incorrectly blocked from accessing this service.</p>
        <p>
        If you believe this is an error, please contact us at
        <a href="mailto:alphafolddb@ebi.ac.uk">alphafolddb@ebi.ac.uk</a>
        and include:
      </p>
      <ol style="display: inline-block; text-align: left; margin: 0 auto 16px; padding-left: 20px;">
        <li>The date and time the issue occurred (including your time zone)</li>
        <li>Your browser and version</li>
        <li>Your device and operating system</li>
        <li>Whether you were using a VPN, proxy, or corporate network</li>
      </ol>
      <p>This information helps us investigate the issue more quickly.</p>
      </div>
    </div>`;
}

function isTrustedClient(platform: PlatformService): boolean {
  const trustedClientValue = safeLocalStorageGet(platform, TRUSTED_CLIENT_KEY);
  return trustedClientValue === 'true';
}

function registerSuccessfulAssessment(platform: PlatformService): void {
  const currentSuccessCount = getRecaptchaSuccessCount(platform);
  const updatedSuccessCount = currentSuccessCount + 1;

  safeLocalStorageSet(platform, RECAPTCHA_SUCCESS_COUNT_KEY, String(updatedSuccessCount));

  if (updatedSuccessCount >= TRUST_THRESHOLD) {
    safeLocalStorageSet(platform, TRUSTED_CLIENT_KEY, 'true');
  }
}

function getRecaptchaSuccessCount(platform: PlatformService): number {
  const rawCount = safeLocalStorageGet(platform, RECAPTCHA_SUCCESS_COUNT_KEY);

  if (!rawCount) {
    return 0;
  }

  const parsedCount = Number.parseInt(rawCount, 10);
  return Number.isNaN(parsedCount) || parsedCount < 0 ? 0 : parsedCount;
}

function safeLocalStorageGet(platform: PlatformService, key: string): string | null {
  try {
    const storage = platform.getLocalStorage();
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeLocalStorageSet(platform: PlatformService, key: string, value: string): void {
  try {
    const storage = platform.getLocalStorage();
    if (storage) {
      storage.setItem(key, value);
    }
  } catch {
    // Ignore storage write errors (e.g., disabled storage) and keep flow functional.
  }
}